import React, { useEffect, useRef } from 'react';
import { notification } from 'antd';
import {
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useDashboardAlerts, Alert } from '../../../hooks/useManufacturingDashboard';

interface ManufacturingDashboardAlertsProps {
  enabled: boolean; // Enable/disable alert toasts
  soundEnabled?: boolean; // Enable/disable sound alerts
  refreshInterval?: number;
}

/**
 * Manufacturing dashboard alert toasts (antd + optional browser Notification).
 * Not the enterprise Notification Platform (`modules/notifications`).
 */
const ManufacturingDashboardAlerts: React.FC<ManufacturingDashboardAlertsProps> = ({
  enabled,
  soundEnabled = true,
  refreshInterval = 30000,
}) => {
  const { data: alerts } = useDashboardAlerts(enabled ? refreshInterval : undefined);
  const [api, contextHolder] = notification.useNotification();
  const previousCriticalCount = useRef<number>(0);
  const notifiedAlerts = useRef<Set<string>>(new Set());

  const getAlertIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      'bottleneck': <ThunderboltOutlined style={{ color: '#faad14' }} />,
      'delay': <ClockCircleOutlined style={{ color: '#ff4d4f' }} />,
      'quality': <ExperimentOutlined style={{ color: '#ff4d4f' }} />,
      'resource': <TeamOutlined style={{ color: '#faad14' }} />,
      'maintenance': <ToolOutlined style={{ color: '#fa8c16' }} />,
    };
    return icons[type] || <InfoCircleOutlined />;
  };

  const playAlertSound = () => {
    if (!soundEnabled) return;
    
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Failed to play alert sound:', error);
    }
  };

  const showNotification = (alert: Alert) => {
    const config: any = {
      message: alert.title,
      description: (
        <div>
          <div>{alert.description}</div>
          {alert.actionRequired && (
            <div style={{ marginTop: 8, fontWeight: 500, color: '#145A54' }}>
              Action: {alert.actionRequired}
            </div>
          )}
        </div>
      ),
      icon: getAlertIcon(alert.type),
      duration: alert.severity === 'critical' ? 0 : 4.5, // Critical alerts don't auto-close
      placement: 'topRight' as const,
    };

    if (alert.severity === 'critical') {
      api.error(config);
      playAlertSound();
    } else if (alert.severity === 'warning') {
      api.warning(config);
    } else {
      api.info(config);
    }
  };

  useEffect(() => {
    if (!enabled || !alerts) return;

    const currentCriticalCount = alerts.critical?.length || 0;

    // Check for new critical alerts
    if (alerts.critical) {
      alerts.critical.forEach(alert => {
        // Only show notification if we haven't notified about this alert before
        if (!notifiedAlerts.current.has(alert.id)) {
          showNotification(alert);
          notifiedAlerts.current.add(alert.id);
        }
      });
    }

    // Show warning notifications for new warnings (but don't sound)
    if (alerts.warnings) {
      alerts.warnings.forEach(alert => {
        if (!notifiedAlerts.current.has(alert.id)) {
          showNotification(alert);
          notifiedAlerts.current.add(alert.id);
        }
      });
    }

    previousCriticalCount.current = currentCriticalCount;

    // Clean up old notifications from the set (keep last 50)
    if (notifiedAlerts.current.size > 50) {
      const alertsArray = Array.from(notifiedAlerts.current);
      notifiedAlerts.current = new Set(alertsArray.slice(-50));
    }
  }, [alerts, enabled]);

  // Request browser notification permission on mount
  useEffect(() => {
    if (enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [enabled]);

  // Show browser notifications for critical alerts
  useEffect(() => {
    if (!enabled || !alerts?.critical || typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      alerts.critical.forEach(alert => {
        if (!notifiedAlerts.current.has(`browser-${alert.id}`)) {
          new Notification(alert.title, {
            body: alert.description,
            icon: '/favicon.ico',
            tag: alert.id, // Prevents duplicate notifications
            requireInteraction: true, // Notification stays until user dismisses
          });
          notifiedAlerts.current.add(`browser-${alert.id}`);
        }
      });
    }
  }, [alerts, enabled]);

  return <>{contextHolder}</>;
};

export default ManufacturingDashboardAlerts;
